import { ROUTES } from "@/constants";

const AUTH_REDIRECT_QUERY_KEY = "redirectUrl";
const AUTH_ROUTE_PATHS = new Set<string>([
  ROUTES.LOGIN,
  ROUTES.FORGOT_PASSWORD,
]);

const getRedirectFromState = (state: unknown) => {
  if (
    typeof state === "object" &&
    state !== null &&
    "from" in state &&
    typeof state.from === "string"
  ) {
    return state.from;
  }

  return null;
};

const sanitizeRedirectPath = (candidate: unknown) => {
  if (typeof candidate !== "string") {
    return null;
  }

  const value = candidate.trim();

  if (!value || !value.startsWith("/") || value.startsWith("//")) {
    return null;
  }

  const baseOrigin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";

  try {
    const parsedUrl = new URL(value, baseOrigin);

    if (
      parsedUrl.origin !== baseOrigin ||
      AUTH_ROUTE_PATHS.has(parsedUrl.pathname)
    ) {
      return null;
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return null;
  }
};

export const resolveAuthRedirect = (search: string, state: unknown) => {
  const searchParams = new URLSearchParams(search);
  const redirectFromQuery = sanitizeRedirectPath(
    searchParams.get(AUTH_REDIRECT_QUERY_KEY),
  );

  if (redirectFromQuery) {
    return redirectFromQuery;
  }

  const redirectFromState = sanitizeRedirectPath(getRedirectFromState(state));
  return redirectFromState ?? ROUTES.HOME;
};

export const buildAuthRedirectSearch = (redirectPath: string) => {
  const normalizedPath = sanitizeRedirectPath(redirectPath);

  if (!normalizedPath || normalizedPath === ROUTES.HOME) {
    return "";
  }

  const searchParams = new URLSearchParams();
  searchParams.set(AUTH_REDIRECT_QUERY_KEY, normalizedPath);
  return `?${searchParams.toString()}`;
};

export const buildAuthRedirectState = (redirectPath: string) => {
  const normalizedPath = sanitizeRedirectPath(redirectPath);

  if (!normalizedPath || normalizedPath === ROUTES.HOME) {
    return undefined;
  }

  return { from: normalizedPath };
};

export const buildAuthRouteTarget = (
  pathname: string,
  redirectPath: string,
) => ({
  pathname,
  search: buildAuthRedirectSearch(redirectPath),
});
